import type { ReactNode } from 'react';
import { Footer, Header } from './Shell.js';

/*
 * Paginas corporativas.
 *
 * O texto e **minuta**: descreve com precisao o que o sistema faz, porque
 * quem escreveu leu o codigo, e e exatamente por isso que precisa de revisao
 * juridica antes de valer. Descrever certo nao e o mesmo que obrigar certo.
 *
 * O aviso no topo nao e rodape em letra miuda de proposito: enquanto estiver
 * la, ninguem publica isto por engano achando que ja esta valendo.
 */

const ATUALIZADO = '28 de outubro de 2026';
const RAZAO = 'FA Tech Ltda';
const CNPJ = '62.103.780/0001-52';

/** O que falta e so o cliente sabe. Visivel, nao escondido num comentario. */
function Pendencia({ children }: { children: ReactNode }): ReactNode {
  return <mark className="pendencia">{children}</mark>;
}

function PaginaLegal({
  titulo,
  resumo,
  children,
}: {
  titulo: string;
  resumo: string;
  children: ReactNode;
}): ReactNode {
  return (
    <>
      <Header compacto />
      <main className="legal">
        <div className="wrap">
          <p className="minuta" role="note">
            <b>Minuta sujeita a revisão jurídica.</b> O texto descreve o funcionamento real da
            plataforma, mas ainda não foi revisado por advogado e não deve ser publicado como
            documento vigente.
          </p>

          <h1>{titulo}</h1>
          <p className="resumo">{resumo}</p>
          <p className="vigencia">
            Última atualização: {ATUALIZADO} · {RAZAO} · CNPJ {CNPJ}
          </p>

          <article>{children}</article>
        </div>
      </main>
      <Footer />
    </>
  );
}

export function Termos(): ReactNode {
  return (
    <PaginaLegal
      titulo="Termos de uso"
      resumo="O que a FA Tech Notas faz por você, o que continua sendo sua responsabilidade, e onde ficam os limites de cada um."
    >
      <section>
        <h2>1. O que este documento rege</h2>
        <p>
          Estes termos regem o uso da plataforma FA Tech Notas, operada por {RAZAO}, CNPJ {CNPJ}.
          Ao criar uma conta, você declara ter poderes para contratar em nome da empresa informada
          no cadastro.
        </p>
      </section>

      <section>
        <h2>2. O que a plataforma faz — e o que não faz</h2>
        <p>
          A plataforma recebe os dados de uma operação, calcula os tributos conforme as tabelas
          vigentes, monta e assina o XML com o certificado digital da sua empresa, transmite o
          documento à SEFAZ do estado ou ao Sefin Nacional, e guarda o resultado.
        </p>
        <p>
          <b>A plataforma não decide o conteúdo fiscal da operação.</b> CFOP, CST, CSOSN, NCM,
          código de serviço, alíquotas e regime tributário dependem da natureza da sua operação e da
          legislação aplicável a ela. Esses dados vêm de você ou do seu sistema, e quem responde por
          eles é você e a sua contabilidade. Não prestamos serviço de contabilidade nem consultoria
          tributária.
        </p>
        <p>
          A autorização de um documento é ato da administração tributária, não nosso. Transmitir
          corretamente não garante autorização: a SEFAZ pode rejeitar, denegar ou estar
          indisponível.
        </p>
      </section>

      <section>
        <h2>3. Conta, credenciais e chaves de API</h2>
        <p>
          A senha de acesso é gerada por nós e enviada ao e-mail informado no cadastro — receber a
          mensagem é o que comprova que o endereço é seu. Troque-a no primeiro acesso.
        </p>
        <p>
          As chaves de API identificam a sua conta e valem como assinatura: qualquer documento
          emitido com uma chave sua é tratado como emitido por você. Guarde-as fora do código-fonte
          e revogue imediatamente, pelo portal, qualquer chave que tenha vazado. Não armazenamos a
          chave em texto claro e não conseguimos recuperá-la — só emitir outra.
        </p>
      </section>

      <section>
        <h2>4. Certificado digital</h2>
        <p>
          O certificado A1 que você envia é usado exclusivamente para assinar os seus documentos
          fiscais e autenticar a conversa com a administração tributária. Ele é cifrado em repouso,
          e cada uso fica registrado em trilha de auditoria.
        </p>
        <p>
          Você pode substituir ou remover o certificado a qualquer momento pelo portal. Sem
          certificado válido não há emissão — a assinatura é requisito legal do documento, não
          recurso da plataforma.
        </p>
      </section>

      <section>
        <h2>5. O que depende de você</h2>
        <ul>
          <li>
            Manter o cadastro da empresa correto, inclusive inscrição estadual, inscrição municipal
            e regime tributário.
          </li>
          <li>
            Obter junto à SEFAZ do seu estado o credenciamento para cada modelo que pretende emitir.
            O credenciamento de NFC-e é separado do de NF-e.
          </li>
          <li>
            Obter e cadastrar o CSC (Código de Segurança do Contribuinte) quando for emitir NFC-e.
            Ele é gerado no portal da SEFAZ e só você pode gerá-lo.
          </li>
          <li>Conferir os documentos emitidos e cancelar dentro do prazo legal quando necessário.</li>
        </ul>
      </section>

      <section>
        <h2>6. Disponibilidade</h2>
        <p>
          Trabalhamos para manter a plataforma disponível, mas a emissão depende de serviços de
          terceiros que não controlamos — as SEFAZ estaduais, o Sefin Nacional e as prefeituras.
          Indisponibilidade desses serviços não é falha da plataforma, e nesses casos o documento
          fica em fila ou entra em contingência, conforme o modelo permitir.
        </p>
        <p>
          Manutenções programadas são avisadas com antecedência. Interrupções não programadas são
          comunicadas assim que identificadas.
        </p>
      </section>

      <section>
        <h2>7. Planos e cobrança</h2>
        <p>
          Cada plano define um volume mensal de documentos e um limite de empresas emitentes.
          Documentos acima do volume contratado são cobrados por excedente, na tarifa vigente no
          momento da emissão. A conta começa em período de teste, sem cobrança, e emitir em produção
          depende de certificado válido e de credenciamento junto à SEFAZ.
        </p>
        <p>
          <Pendencia>
            A definir antes de publicar: ciclo de faturamento, prazo de pagamento, índice e
            periodicidade de reajuste, e consequência do inadimplemento.
          </Pendencia>
        </p>
      </section>

      <section>
        <h2>8. Guarda dos documentos</h2>
        <p>
          Os XMLs autorizados e os respectivos protocolos ficam armazenados por, no mínimo, cinco
          anos, em meio que impede alteração ou exclusão antes do fim do prazo. Esse prazo
          acompanha a exigência legal de guarda dos documentos fiscais eletrônicos e vale também
          depois do encerramento da conta.
        </p>
      </section>

      <section>
        <h2>9. Encerramento e exportação</h2>
        <p>
          Você pode encerrar a conta a qualquer momento. Antes do encerramento, exporte os seus
          documentos pelo portal — a exportação continua disponível durante o prazo legal de guarda,
          mas a emissão de novos documentos cessa imediatamente.
        </p>
        <p>
          Podemos suspender a conta em caso de uso que comprometa a plataforma, de indício de fraude
          ou de determinação de autoridade competente, comunicando o motivo.
        </p>
      </section>

      <section>
        <h2>10. Limites de responsabilidade</h2>
        <p>
          Não respondemos por tributo calculado a partir de dado incorreto enviado por você, por
          recusa da administração tributária, por indisponibilidade de serviço de terceiro, nem por
          consequência de credencial sua que tenha vazado.
        </p>
        <p>
          <Pendencia>
            A definir antes de publicar: teto de responsabilidade, prazo para reclamação e hipóteses
            de exclusão.
          </Pendencia>
        </p>
      </section>

      <section>
        <h2>11. Mudanças nestes termos</h2>
        <p>
          Alterações relevantes são comunicadas por e-mail e no portal, com antecedência razoável
          antes de entrarem em vigor. Continuar usando a plataforma depois disso significa aceitar a
          nova versão.
        </p>
      </section>

      <section>
        <h2>12. Lei aplicável e foro</h2>
        <p>
          Aplica-se a legislação brasileira. <Pendencia>Foro a definir pelo jurídico.</Pendencia>
        </p>
      </section>
    </PaginaLegal>
  );
}

export function Privacidade(): ReactNode {
  return (
    <PaginaLegal
      titulo="Política de privacidade"
      resumo="Quais dados pessoais passam pela plataforma, por que passam, com quem são compartilhados e por quanto tempo ficam."
    >
      <section>
        <h2>1. Dois papéis diferentes</h2>
        <p>
          A Lei Geral de Proteção de Dados separa quem decide o tratamento de quem executa. Essa
          distinção muda quem você procura em cada caso, então ela vem antes de tudo:
        </p>
        <ul>
          <li>
            <b>Dos dados da sua empresa e das pessoas da sua equipe,</b> somos controladores. Nós
            decidimos tratá-los para manter a conta, cobrar e dar suporte.
          </li>
          <li>
            <b>Dos dados que aparecem nos documentos fiscais</b> — nome, CPF ou CNPJ e endereço de
            quem comprou de você — somos operadores. Quem decide emitir é você; nós processamos a
            seu comando. O titular que quiser exercer direitos sobre esses dados deve procurar
            primeiro a sua empresa.
          </li>
        </ul>
      </section>

      <section>
        <h2>2. Que dados tratamos</h2>
        <ul>
          <li>
            <b>Cadastro:</b> razão social, CNPJ, inscrições, endereço, nome e e-mail do responsável.
          </li>
          <li>
            <b>Documentos fiscais:</b> todo o conteúdo que você envia para emissão, incluindo a
            identificação do destinatário quando ela consta da nota.
          </li>
          <li>
            <b>Certificado digital:</b> o arquivo A1 e a senha, cifrados em repouso.
          </li>
          <li>
            <b>Registros de uso:</b> data, hora, endereço IP, ação executada e autor, para
            auditoria e segurança.
          </li>
        </ul>
        <p>
          Não tratamos dados pessoais sensíveis e não usamos os seus dados para treinar modelos nem
          para publicidade.
        </p>
      </section>

      <section>
        <h2>3. Por que tratamos</h2>
        <p>
          Cadastro e registros de uso: execução do contrato e legítimo interesse em manter a
          plataforma segura. Documentos fiscais e sua guarda: cumprimento de obrigação legal e
          regulatória. Comunicações sobre o serviço: execução do contrato.
        </p>
      </section>

      <section>
        <h2>4. Com quem compartilhamos</h2>
        <ul>
          <li>
            <b>Administração tributária</b> — SEFAZ estaduais, Sefin Nacional e prefeituras. É o
            destino do documento; sem isso não há nota fiscal.
          </li>
          <li>
            <b>Infraestrutura</b> — hospedagem e armazenamento dos arquivos, em servidores no
            Brasil.
          </li>
          <li>
            <b>Envio de e-mail</b> — para as mensagens transacionais da conta.
          </li>
          <li>
            <b>Autoridade competente</b> — mediante requisição legal.
          </li>
        </ul>
        <p>Não vendemos dados pessoais e não os cedemos para uso de terceiros em fim próprio.</p>
      </section>

      <section>
        <h2>5. Por quanto tempo</h2>
        <p>
          Documentos fiscais e seus protocolos: no mínimo cinco anos, em armazenamento que impede
          alteração ou exclusão antes do prazo, conforme a exigência de guarda. Registros de
          auditoria: pelo prazo necessário à finalidade de segurança. Dados de cadastro: enquanto a
          conta existir e pelo prazo legal aplicável depois do encerramento.
        </p>
        <p>
          Pedido de exclusão não alcança o que a lei manda guardar — e documento fiscal é
          exatamente esse caso.
        </p>
      </section>

      <section>
        <h2>6. Segurança</h2>
        <p>
          Certificados e senhas são cifrados em repouso e nunca trafegam em mensagem, registro ou
          linha de comando. O acesso aos dados de cada conta é isolado no banco por regra do próprio
          banco, não apenas pela aplicação. Toda ação da nossa equipe sobre a conta de um cliente
          exige justificativa e fica registrada em trilha imutável.
        </p>
      </section>

      <section>
        <h2>7. Seus direitos</h2>
        <p>
          A LGPD garante ao titular confirmação da existência de tratamento, acesso, correção,
          anonimização, portabilidade, informação sobre compartilhamento e revogação de
          consentimento, quando for o caso.
        </p>
        <p>
          <Pendencia>
            A definir antes de publicar: nome do encarregado (DPO) e o canal de atendimento ao
            titular — e-mail ou endereço para correspondência.
          </Pendencia>
        </p>
      </section>

      <section>
        <h2>8. Mudanças nesta política</h2>
        <p>
          Alterações relevantes são comunicadas por e-mail e no portal antes de entrarem em vigor. A
          data no topo desta página indica a versão vigente.
        </p>
      </section>
    </PaginaLegal>
  );
}
